import { ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API } from '@/lib/api';
import { dinero } from '@/lib/precioCobertura';
import { badgeCls, tierColor } from './comunes';

/** Las coberturas del comerciante, con el certificado, la foto y el reclamo. */
export function TablaCoberturas({ reports, token, onPhoto, onClaim }) {
  // El certificado pide sesion: se baja con el token y se escribe en una
  // pestana nueva, en vez de poner el token en la direccion.
  const abrirCertificado = (vn) => {
    const w = window.open('');
    fetch(`${API}/insurance/selletrans/report/${vn}/certificate`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.text())
      .then(h => w.document.write(h));
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-900/60 text-slate-500 text-xs uppercase tracking-wide">
          <tr>
            <th className="px-4 py-3 text-left">Verification #</th>
            <th className="px-4 py-3 text-left">Tier</th>
            <th className="px-4 py-3 text-left">Coverage</th>
            <th className="px-4 py-3 text-left">Premium</th>
            <th className="px-4 py-3 text-left">Route</th>
            <th className="px-4 py-3 text-left">Tracking #</th>
            <th className="px-4 py-3 text-left">Issued</th>
            <th className="px-4 py-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody data-testid="policies-rows">
          {reports.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No coverages yet. Click "Add coverage" to get started.</td></tr>}
          {reports.map(r => (
            <tr key={r.verification_number} className="border-t border-slate-100 dark:border-slate-800" data-testid={`policy-row-${r.verification_number}`}>
              <td className="px-4 py-3"><span className="font-mono text-xs font-semibold">{r.verification_number}</span></td>
              <td className="px-4 py-3"><span className={`px-2 py-1 rounded text-xs font-bold text-white bg-gradient-to-br ${tierColor(r.coverage_tier)}`}>{r.coverage_tier}</span></td>
              <td className="px-4 py-3 tabular-nums">${(r.coverage_amount || 0).toLocaleString()}</td>
              <td className="px-4 py-3 tabular-nums">{r.status === 'void' ? <span className="text-xs text-slate-400">Void</span> : r.merchant_charge ? dinero(r.merchant_charge.amount) : '—'}</td>
              <td className="px-4 py-3 text-xs">{r.origin_zip || '—'} → {r.destination_zip}</td>
              <td className="px-4 py-3 font-mono text-xs">{r.tracking_number || '—'}</td>
              <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{new Date(r.issued_at).toLocaleString()}</td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => abrirCertificado(r.verification_number)} data-testid={`cert-${r.verification_number}`}>
                    <ExternalLink className="w-3 h-3 mr-1" />Certificate
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onPhoto(r)} data-testid={`foto-${r.verification_number}`}>Item photo</Button>
                  <Button size="sm" variant="outline" onClick={() => onClaim(r)} data-testid={`claim-${r.verification_number}`}>File claim</Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Los reclamos del comerciante y en que estan. */
export function TablaReclamos({ claims }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
          <tr>
            <th className="px-4 py-3 text-left">Claim #</th>
            <th className="px-4 py-3 text-left">Coverage #</th>
            <th className="px-4 py-3 text-left">Type</th>
            <th className="px-4 py-3 text-left">Status</th>
            <th className="px-4 py-3 text-left">Amount</th>
            <th className="px-4 py-3 text-left">Filed</th>
          </tr>
        </thead>
        <tbody data-testid="claims-rows">
          {claims.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No claims filed. Open a coverage and click "File claim" to begin.</td></tr>}
          {claims.map(c => (
            <tr key={c.claim_number} className="border-t border-slate-100 dark:border-slate-800" data-testid={`claim-row-${c.claim_number}`}>
              <td className="px-4 py-3 font-mono text-xs">{c.claim_number}</td>
              <td className="px-4 py-3 font-mono text-xs">{c.verification_number}</td>
              <td className="px-4 py-3 text-xs uppercase">{(c.claim_type || '').replaceAll('_', ' ')}</td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badgeCls(c.status)}`}>{(c.status || '').toUpperCase()}</span>
                {c.reviewer_notes && <span className="block text-[11px] text-slate-500 mt-1 max-w-xs" data-testid={`claim-notes-${c.claim_number}`}>{c.reviewer_notes}</span>}
              </td>
              <td className="px-4 py-3 tabular-nums">${c.amount_claimed?.toFixed(2)} <span className="text-slate-400 text-xs">est ${c.payable_estimate?.toFixed(2)}</span></td>
              <td className="px-4 py-3 text-xs text-slate-500">
                {new Date(c.filed_at).toLocaleDateString()}
                {c.records_due_at && <span className="block text-[11px]">records by {new Date(c.records_due_at).toLocaleDateString()}</span>}
                {c.late_notice && <span className="block text-[11px] text-amber-600" data-testid={`claim-late-${c.claim_number}`}>late notice · reviewed</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
